import {
  AbstractMesh,
  ActionManager,
  Color4,
  CreateBoxVertexData,
  CSG2 as CSG,
  ExecuteCodeAction,
  IsCSG2Ready,
  ImportMeshAsync,
  Material,
  Mesh,
  MeshBuilder,
  Scene,
  SceneSerializer,
  Vector3,
  Matrix,
  BoundingInfo,
  Quaternion,
} from '@babylonjs/core'
import {
  BuildingElementType,
  BuildingElementSubType,
  BuildingTool,
  BuildingObjectTool,
  BuildingStructuralObjectTool,
  BuildingEditingTool,
  Wall,
} from '../models/Construction'
import { SceneManager } from './sceneManager'
import {
  BuildingStructuralTypes,
  RectangleFramingElementType,
} from '../models/Structural'
import { v4 as uuid } from 'uuid'
import store from '../store'
import { calculateContour, deg2Rad } from '../utils/MathUtil'
import { MaterialManager } from './materials/materialManager'

export const getBuildingBoundingBox = () => {
  const scene = SceneManager.getInstance().getScene()
  const buildingItems =
    scene?.meshes.filter((mesh) => mesh.isVisible && isStoreyMesh(mesh)) ?? []

  const minVector = new Vector3(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
  )
  const maxVector = new Vector3(
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  )

  for (const buildingItem of buildingItems) {
    const boundingVectors = buildingItem.getHierarchyBoundingVectors(
      true,
      (mesh) => !isRoofMesh(mesh),
    )

    minVector.minimizeInPlace(boundingVectors.min)
    maxVector.maximizeInPlace(boundingVectors.max)
  }

  const distance = Vector3.Distance(minVector, maxVector)
  if (!isFinite(distance)) {
    return null
  }

  return { min: minVector, max: maxVector }
}

export const computeBoundingBox = (
  meshes: AbstractMesh[],
  localCoordinates?: boolean,
) => {
  const min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE)
  const max = new Vector3(
    -Number.MAX_VALUE,
    -Number.MAX_VALUE,
    -Number.MAX_VALUE,
  )

  meshes.forEach((mesh) => {
    if (!mesh.getBoundingInfo) return

    const boundingInfo = mesh.getBoundingInfo()
    const boundingBox = boundingInfo.boundingBox

    min.minimizeInPlace(
      localCoordinates ? boundingBox.minimum : boundingBox.minimumWorld,
    )
    max.maximizeInPlace(
      localCoordinates ? boundingBox.maximum : boundingBox.maximumWorld,
    )
  })

  return { min, max }
}

export const isGroundMesh = (mesh: AbstractMesh) => {
  return !!(mesh.metadata && mesh.metadata.type === BuildingElementType.Floor)
}

export const isWallMesh = (mesh: AbstractMesh | null) => {
  return (
    !!mesh && mesh.metadata && mesh.metadata.type === BuildingElementType.Wall
  )
}

export const isExteriorWallMesh = (mesh: AbstractMesh) => {
  return (
    isWallMesh(mesh) &&
    mesh.metadata.subType === BuildingElementSubType.Exterior
  )
}

export const isInteriorWallMesh = (mesh: AbstractMesh) => {
  return (
    isWallMesh(mesh) &&
    mesh.metadata.subType === BuildingElementSubType.Interior
  )
}

export const isUnknownWallMesh = (mesh: AbstractMesh) => {
  return (
    isWallMesh(mesh) && mesh.metadata.subType === BuildingElementSubType.Unknown
  )
}

export const isWindowMesh = (mesh: AbstractMesh) => {
  return !!(
    mesh.metadata &&
    (mesh.metadata.type === BuildingElementType.Window ||
      mesh.metadata.sourceType === BuildingElementType.Window)
  )
}

export const isDoorMesh = (mesh: AbstractMesh) => {
  return !!(
    mesh.metadata &&
    (mesh.metadata.type === BuildingElementType.Door ||
      mesh.metadata.sourceType === BuildingElementType.Door)
  )
}

export const isRoofMesh = (mesh: AbstractMesh) => {
  return !!(mesh.metadata && mesh.metadata.type === BuildingElementType.Roof)
}

export const isStoreyMesh = (mesh: AbstractMesh) => {
  return !!(mesh.metadata && mesh.metadata.type === BuildingElementType.Storey)
}

export const isSpanMesh = (mesh: AbstractMesh) => {
  return !!(mesh.metadata && mesh.metadata.type === BuildingElementType.Span)
}

export const isUtilityMesh = (mesh: AbstractMesh) => {
  return !!(mesh.metadata && mesh.metadata.type === BuildingElementType.Utility)
}

export const isStructuralMesh = (mesh: AbstractMesh) => {
  return !!(
    mesh.metadata && mesh.metadata.type === BuildingElementType.Structural
  )
}

export const isStructuralMaterial = (mat: Material) => {
  return !!(
    mat.metadata && mat.metadata.type === BuildingElementType.Structural
  )
}

export const isShearWallMesh = (mesh: AbstractMesh) => {
  return (
    isStructuralMesh(mesh) &&
    mesh.metadata.structuralType === BuildingStructuralTypes.ShearWall
  )
}

export const isLoadBearingWallMesh = (mesh: AbstractMesh) => {
  return (
    isStructuralMesh(mesh) &&
    mesh.metadata.structuralType === BuildingStructuralTypes.LoadBearingWall
  )
}

export const isBeamMesh = (mesh: AbstractMesh) => {
  return (
    isStructuralMesh(mesh) &&
    mesh.metadata.structuralType === BuildingStructuralTypes.Beam
  )
}

export const isHeaderMesh = (mesh: AbstractMesh) => {
  return (
    isStructuralMesh(mesh) &&
    mesh.metadata.structuralType === BuildingStructuralTypes.Header
  )
}

export const isFoundationMesh = (mesh: AbstractMesh) => {
  return (
    isStructuralMesh(mesh) &&
    mesh.metadata.structuralType === BuildingStructuralTypes.Foundation
  )
}

export const isStructuralRoofMesh = (mesh: AbstractMesh) => {
  return (
    isStructuralMesh(mesh) &&
    mesh.metadata.structuralType === BuildingStructuralTypes.Roof
  )
}

export const isRidgeMesh = (mesh: AbstractMesh) => {
  return (
    isStructuralRoofMesh(mesh) &&
    mesh.metadata.rectangleFramingElementType ===
      RectangleFramingElementType.Ridge
  )
}

export const isSketchMesh = (mesh: AbstractMesh) => {
  return !!(mesh.metadata && mesh.metadata.type === BuildingElementType.Sketch)
}

export const isWallGapMesh = (mesh: AbstractMesh | null) => {
  return (
    !!mesh &&
    mesh.metadata &&
    mesh.metadata.type === BuildingElementType.WallGap
  )
}

export const isWallOrWallGap = (mesh: AbstractMesh) => {
  return isWallMesh(mesh) || isWallGapMesh(mesh)
}

export const isBimObjectMesh = (mesh: AbstractMesh) => {
  return !!(
    mesh.metadata && mesh.metadata.type === BuildingElementType.BimObject
  )
}

export const isGridMesh = (mesh: AbstractMesh) => {
  return mesh.name === 'Grid'
}

export const isSelectableMesh = (mesh: AbstractMesh | null) => {
  if (!mesh || !mesh.metadata) {
    return false
  }

  const { type, sourceType, structuralType } = mesh.metadata

  const selectableMeshes = new Set([
    BuildingElementType.Wall,
    BuildingElementType.Door,
    BuildingElementType.Window,
    BuildingElementType.Roof,
    BuildingElementType.Structural,
  ])

  const selectableStructuralMeshes = new Set([
    BuildingStructuralTypes.Beam,
    BuildingStructuralTypes.Header,
    BuildingStructuralTypes.Joist,
    BuildingStructuralTypes.Stud,
  ])

  const isStructural = type === BuildingElementType.Structural

  return (
    (selectableMeshes.has(type) || selectableMeshes.has(sourceType)) &&
    (!isStructural || selectableStructuralMeshes.has(structuralType))
  )
}

export const isSelectionIgnored = (tool: BuildingTool | null) => {
  const selectionIgnoredTools = [
    BuildingObjectTool.Walls,
    BuildingObjectTool.Windows,
    BuildingObjectTool.Doors,
    BuildingStructuralObjectTool.Beams,
    BuildingEditingTool.Marquee,
  ]

  return Boolean(tool && selectionIgnoredTools.includes(tool))
}

export const waitForLongPickDownDelay = (
  mesh: Mesh,
  ms: number = 250,
  threshold: number = 1,
) => {
  return new Promise<void>((resolve, reject) => {
    const scene = SceneManager.getInstance().getScene()
    const actionManager = mesh.actionManager
    if (!scene || !actionManager) {
      return reject()
    }

    const startPointerTime = Date.now()
    const startPointX = scene.pointerX
    const startPointY = scene.pointerY

    const onPickUpTrigger = new ExecuteCodeAction(
      ActionManager.OnPickUpTrigger,
      handleAbort,
    )

    actionManager.registerAction(onPickUpTrigger)

    const timeoutId = setTimeout(() => {
      actionManager.unregisterAction(onPickUpTrigger)

      if (
        Date.now() - startPointerTime >= ms &&
        Math.abs(startPointX - scene.pointerX) < threshold &&
        Math.abs(startPointY - scene.pointerY) < threshold
      ) {
        resolve()
      } else {
        reject()
      }
    }, ms)

    function handleAbort() {
      actionManager?.unregisterAction(onPickUpTrigger)
      clearTimeout(timeoutId)
      reject()
    }
  })
}

type CreateWallGapOptions = Pick<
  Wall,
  | 'id'
  | 'length'
  | 'height'
  | 'thickness'
  | 'center_point'
  | 'bottom_elevation'
  | 'angle'
  | 'type'
>

// TODO: Maybe move to better place
export const createWallGaps = () => {
  const materialManager = MaterialManager.getInstance()
  const scene = SceneManager.getInstance().getScene()
  if (!scene) {
    return
  }

  const threshold = 0.001
  scene.meshes.filter((m) => isWallGapMesh(m)).forEach((m) => m.dispose())

  // Groups walls by coordinate (x or z) and angle
  const groupWalls = (walls: Wall[]) => {
    const byX = new Map<string, Wall[]>()
    const byZ = new Map<string, Wall[]>()

    const normalizeAngle = (angle: number): number => {
      // Normalize angle to 0–180 range, treating 0 and 180 as equivalent
      const modAngle = angle % 360
      if (
        Math.abs(modAngle - 180) <= threshold ||
        Math.abs(modAngle) <= threshold
      ) {
        return 0
      }
      return modAngle
    }

    const findOrCreateGroupKey = (
      map: Map<string, Wall[]>,
      coord: number,
      angle: number,
    ): string => {
      const normAngle = normalizeAngle(angle)
      for (const key of Array.from(map.keys())) {
        const [keyCoordStr, keyAngleStr] = key.split('|')
        const keyCoord = parseFloat(keyCoordStr)
        const keyAngle = parseFloat(keyAngleStr)
        if (
          Math.abs(keyCoord - coord) <= threshold &&
          Math.abs(keyAngle - normAngle) <= threshold
        ) {
          return key
        }
      }
      return `${coord}|${normAngle}`
    }

    for (const wall of walls) {
      const x = wall.center_point[0]
      const z = wall.center_point[1]
      const { angle } = wall

      const xKey = findOrCreateGroupKey(byX, x, angle)
      if (!byX.has(xKey)) byX.set(xKey, [])
      byX.get(xKey)!.push(wall)

      const zKey = findOrCreateGroupKey(byZ, z, angle)
      if (!byZ.has(zKey)) byZ.set(zKey, [])
      byZ.get(zKey)!.push(wall)
    }

    return { byX, byZ }
  }

  const filterAndSortGroups = (groups: Map<string, Wall[]>, sortBy: number) => {
    return Array.from(groups.values())
      .filter((walls) => walls.length > 1)
      .map((walls) =>
        walls
          .slice()
          .sort((a, b) => a.center_point[sortBy] - b.center_point[sortBy]),
      )
  }

  const createWallsGap = (
    gap: CreateWallGapOptions,
    storeyMesh: Mesh,
    wallIds: string[],
  ): Mesh => {
    const {
      id,
      length,
      height,
      thickness,
      center_point,
      bottom_elevation,
      angle,
      type: subType,
    } = gap
    const existingWallMesh = scene.getMeshById(id) as Mesh
    if (existingWallMesh && !existingWallMesh.isDisposed()) {
      return existingWallMesh
    }

    const gapMesh = MeshBuilder.CreateBox(
      'Walls Gap',
      {
        width: length,
        height: height,
        depth: thickness,
        faceColors: [
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(0, 0, 0, 255),
          Color4.FromInts(255, 255, 255, 255),
        ],
      },
      scene,
    )

    gapMesh.id = id
    gapMesh.setParent(storeyMesh)
    gapMesh.position.set(
      center_point[0],
      bottom_elevation + height / 2,
      center_point[1],
    )
    gapMesh.rotation.y = deg2Rad(angle)
    gapMesh.computeWorldMatrix(true)
    gapMesh.material = materialManager.getTransparentMaterial()

    const gapContour = calculateContour(center_point, thickness, length, angle)

    const gapDirection = gapContour[1].subtract(gapContour[0]).normalize()
    const gapNormal = gapContour[0].subtract(gapContour[3]).normalize()

    gapMesh.metadata = {
      subType,
      isHidden: false,
      type: BuildingElementType.WallGap,
      wallIds,
      wallDirection: gapDirection,
      wallNormal: gapNormal,
    }

    return gapMesh
  }

  const createWallsGaps = (walls: Wall[], storeyMesh: Mesh) => {
    for (let i = 0; i < walls.length - 1; i++) {
      const wallA = walls[i]
      const wallB = walls[i + 1]
      const angle = Math.min(wallA.angle, wallB.angle)
      const height = Math.min(wallA.height, wallB.height)
      const thickness = Math.min(wallA.thickness, wallB.thickness)
      const bottom_elevation = Math.min(
        wallA.bottom_elevation,
        wallB.bottom_elevation,
      )
      let length: number
      let center_point: number[]
      if (Math.abs(wallA.center_point[0] - wallB.center_point[0]) < threshold) {
        const minWallB = wallB.center_point[1] - wallB.length / 2
        const maxWallA = wallA.center_point[1] + wallA.length / 2
        length = Math.abs(minWallB - maxWallA)
        center_point = [
          wallA.center_point[0],
          maxWallA + (minWallB - maxWallA) / 2,
        ]
      } else {
        const minWallB = wallB.center_point[0] - wallB.length / 2
        const maxWallA = wallA.center_point[0] + wallA.length / 2
        length = Math.abs(minWallB - maxWallA)
        center_point = [
          maxWallA + (minWallB - maxWallA) / 2,
          wallA.center_point[1],
        ]
      }

      createWallsGap(
        {
          id: uuid(),
          length,
          height,
          thickness,
          angle,
          center_point,
          bottom_elevation,
          type: wallA.type,
        },
        storeyMesh,
        [wallA.id, wallB.id],
      )
    }
  }

  const building = store.getState().construction.convertData.building
  for (const storey of building.storeys) {
    const grouped = groupWalls(storey.walls)
    const filteredByX = filterAndSortGroups(grouped.byX, 1)
    const filteredByZ = filterAndSortGroups(grouped.byZ, 0)

    const storeyMesh = scene.getMeshById(storey.id)
    if (!storeyMesh) continue
    for (const walls of filteredByX) {
      createWallsGaps(walls, storeyMesh as Mesh)
    }
    for (const walls of filteredByZ) {
      createWallsGaps(walls, storeyMesh as Mesh)
    }
  }
}

/**
 * Cuts holes inside wall mesh from window and door meshes
 * @param wallMesh wall mesh to be cutted
 * @param openingMeshes window or door meshes
 * @param recreateWallMeshVertexData if true will recreate wall mesh base geometry using bounding box extends. Otherwise will use mesh to create CSG object (Default: false)
 * @returns
 */
export const cutOpeningHolesInWallMesh = (
  wallMesh: Mesh,
  openingMeshes: Mesh[],
  recreateWallMeshVertexData: boolean = false,
) => {
  if (!IsCSG2Ready()) {
    console.warn(
      'WallMesh hole cutting is not done because the CSG2 modue is not initialized',
    )
    return
  }

  if (!openingMeshes.length) {
    return
  }

  let wallCSG: CSG
  if (recreateWallMeshVertexData) {
    const boundingBox = wallMesh.getBoundingInfo().boundingBox
    const {
      x: halfLength,
      y: halfHeight,
      z: halfThickness,
    } = boundingBox.extendSize
    const vertexData = CreateBoxVertexData({
      width: halfLength * 2,
      height: halfHeight * 2,
      depth: halfThickness * 2,
      faceColors: [
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(0, 0, 0, 255),
        Color4.FromInts(255, 255, 255, 255),
      ],
    })
    vertexData.transform(wallMesh.getWorldMatrix())
    wallCSG = CSG.FromVertexData(vertexData)
  } else {
    wallCSG = CSG.FromMesh(wallMesh)
  }

  openingMeshes.forEach((openingMesh) => {
    let openingMeshWithGeometry
    let isReplaced = false
    if (!isBimObjectMesh(openingMesh)) {
      openingMeshWithGeometry = openingMesh
    } else {
      if (openingMesh.metadata.templateMeshId) {
        openingMeshWithGeometry = openingMesh
          .getScene()
          .getMeshById(openingMesh.metadata.templateMeshId) as Mesh
      } else {
        openingMeshWithGeometry = createBimObjectTemplateMesh(openingMesh)
        isReplaced = true
      }
    }

    const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
    const { z: wallThickness } = wallBoundingBox.extendSize
    const openingBoundingBox =
      openingMeshWithGeometry.getBoundingInfo().boundingBox
    const { z: thickness } = openingBoundingBox.extendSize
    openingMeshWithGeometry.scaling.z =
      wallThickness / thickness + 1 / thickness

    const openingCSG = CSG.FromMesh(openingMeshWithGeometry)
    openingMeshWithGeometry.scaling.z = 1

    const tmpCSG = wallCSG.subtract(openingCSG)
    openingCSG.dispose()
    wallCSG.dispose()
    wallCSG = tmpCSG
    if (isReplaced) {
      removeBimObjectTemplateMesh(openingMesh)
    }
  })

  const vertexData = wallCSG.toVertexData({ rebuildNormals: true })
  wallCSG.dispose()

  const invertTransform = wallMesh.getWorldMatrix().clone().invert()

  vertexData.transform(invertTransform)
  vertexData.applyToMesh(wallMesh)
}
export const serializeMeshWithMaterials = (mesh: AbstractMesh) => {
  const meshData = SceneSerializer.SerializeMesh(mesh, false, true)

  // Collect all materials used by the mesh
  const materials: Set<Material> = new Set()
  mesh.getChildMeshes().forEach((child) => {
    if (child.material) {
      materials.add(child.material)
    }
  })

  const materialData: any[] = []
  materials.forEach((mat) => {
    materialData.push(mat.serialize())
  })

  return JSON.stringify({
    mesh: meshData,
    materials: materialData,
  })
}

export const importMeshWithMaterials = async (
  serializedData: string,
  targetScene: Scene,
) => {
  const data = JSON.parse(serializedData)

  const materialMap: any = {}
  data.materials.forEach((matData: any) => {
    const mat = Material.Parse(matData, targetScene, '')
    if (mat) {
      materialMap[mat.id] = mat
    }
  })

  const result = await ImportMeshAsync(
    'data:' + JSON.stringify(data.mesh),
    targetScene,
  )

  result.meshes.forEach((mesh) => {
    if (mesh.material && materialMap[mesh.material.id]) {
      mesh.material = materialMap[mesh.material.id]
    }
  })

  return result.meshes
}

export const createBimObjectTemplateMesh = (bimObjectMesh: AbstractMesh) => {
  const boundingVectors = computeBoundingBox(
    bimObjectMesh.getChildMeshes(),
    true,
  )
  const scaling = new Vector3(1, 1, 1)
  const position = Vector3.Zero()
  const rotation = new Quaternion()
  bimObjectMesh.getWorldMatrix().decompose(scaling, rotation, position)
  const scalingMatrix = Matrix.Scaling(scaling.x, scaling.y, scaling.z)

  const min = Vector3.TransformCoordinates(boundingVectors.min, scalingMatrix)
  const max = Vector3.TransformCoordinates(boundingVectors.max, scalingMatrix)
  const boundingInfo = new BoundingInfo(
    boundingVectors.min,
    boundingVectors.max,
    bimObjectMesh.getWorldMatrix(),
  )
  // Calculate size
  const size = max.subtract(min)

  // Create box with the same size
  const box = MeshBuilder.CreateBox(
    'box',
    {
      width: size.x,
      height: size.y,
      depth: size.z,
      faceColors: [
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(255, 255, 255, 255),
        Color4.FromInts(0, 0, 0, 255),
        Color4.FromInts(255, 255, 255, 255),
      ],
    },
    bimObjectMesh.getScene(),
  )

  box.rotationQuaternion = rotation.clone()
  box.position = boundingInfo.boundingBox.centerWorld
  box.setParent(bimObjectMesh.parent)
  box.id = uuid()
  box.material = MaterialManager.getInstance().getTransparentMaterial()
  box.isPickable = true
  box.actionManager = new ActionManager(box.getScene())

  bimObjectMesh.metadata.templateMeshId = box.id

  return box
}

export const removeBimObjectTemplateMesh = (bimObjectMesh: AbstractMesh) => {
  if (bimObjectMesh.metadata && bimObjectMesh.metadata.templateMeshId) {
    const templateMesh = bimObjectMesh
      .getScene()
      .getMeshById(bimObjectMesh.metadata.templateMeshId)
    if (templateMesh) {
      templateMesh.actionManager?.dispose()
      templateMesh.dispose()
      bimObjectMesh.metadata.templateMeshId = undefined
    }
  }
}

export const syncronizeBimObjectMeshWithTemplate = (
  bimObjectMesh: AbstractMesh,
) => {
  if (bimObjectMesh.metadata && bimObjectMesh.metadata.templateMeshId) {
    const templateMesh = bimObjectMesh
      .getScene()
      .getMeshById(bimObjectMesh.metadata.templateMeshId)
    const parent = bimObjectMesh.parent
    bimObjectMesh.setParent(null)
    if (templateMesh) {
      let min
      let max
      let targetCenter

      const targetBoundingBox = templateMesh.getBoundingInfo().boundingBox
      min = targetBoundingBox.minimum
      max = targetBoundingBox.maximum
      targetCenter = targetBoundingBox.centerWorld

      const scaling = new Vector3(1, 1, 1)
      const position = Vector3.Zero()
      const rotation = new Quaternion()
      templateMesh.getWorldMatrix().decompose(scaling, rotation, position)

      let bimElement = bimObjectMesh
        .getScene()
        ?.getMeshById(bimObjectMesh.metadata.bimObjectId)
      if (!bimElement) {
        console.error('Can not find BIM object')
        return
      }

      let boundingVectors = bimElement.getHierarchyBoundingVectors()
      const scalingX =
        (max.x - min.x) / (boundingVectors.max.x - boundingVectors.min.x)
      const scalingY =
        (max.y - min.y) / (boundingVectors.max.y - boundingVectors.min.y)
      const scalingZ =
        (max.z - min.z) / (boundingVectors.max.z - boundingVectors.min.z)
      bimObjectMesh.scaling.set(scalingX, scalingY, scalingZ)
      bimObjectMesh.rotationQuaternion = rotation.clone()

      const size = boundingVectors.max.subtract(boundingVectors.min)
      const center = boundingVectors.min.add(size.scale(0.5))
      const offset = center.multiply(bimObjectMesh.scaling)
      let matrix: Matrix = Matrix.Identity()
      Matrix.FromQuaternionToRef(rotation, matrix)
      const rotatedOffset = Vector3.TransformCoordinates(offset, matrix)
      bimObjectMesh.position = targetCenter.subtract(rotatedOffset)

      bimObjectMesh.setParent(parent)
    }
  }
}
